import { TransactionResponse } from '@ethersproject/abstract-provider'
import { BigNumber } from '@ethersproject/bignumber'
import { t } from '@lingui/macro'
import { Trade } from '@uniswap/router-sdk'
import { Currency, Percent, TradeType } from '@uniswap/sdk-core'
import { SwapRouter } from '@uniswap/universal-router-sdk'
import { FeeOptions, toHex } from '@uniswap/v3-sdk'
import { UNIVERSAL_ROUTER_ADDRESS } from 'constants/addresses'
import { useCallback } from 'react'
import { trace } from 'tracing'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import isZero from 'utils/isZero'
import { didUserReject, swapErrorToUserReadableMessage } from 'utils/swapErrorToUserReadableMessage'
import { getInputTokenInfo, injectInputFee } from 'utils/universalRouter'
import { useAccount } from 'wagmi'

import { useEthersSigner } from '../connection/wallet-connect'
import { PermitSignature } from './usePermitAllowance'

class GasEstimationError extends Error {
  constructor() {
    super(t`Your swap is expected to fail.`)
  }
}

class ModifiedSwapError extends Error {
  constructor() {
    super(
      t`Your swap was modified through your wallet. If this was a mistake, please cancel immediately or risk losing your funds.`
    )
  }
}

interface SwapOptions {
  slippageTolerance: Percent
  deadline?: BigNumber
  permit?: PermitSignature
  feeOptions?: FeeOptions
}

export function useUniversalRouterSwapCallback(
  trade: Trade<Currency, Currency, TradeType> | undefined,
  fiatValues: { amountIn: number | undefined; amountOut: number | undefined },
  options: SwapOptions
) {
  const { address: account, chainId } = useAccount()
  const provider = useEthersSigner()

  return useCallback(async (): Promise<TransactionResponse> => {
    return trace(
      'swap.send',
      async ({ setTraceData, setTraceStatus, setTraceError }) => {
        try {
          if (!account) throw new Error('missing account')
          if (!chainId) throw new Error('missing chainId')
          if (!provider) throw new Error('missing provider')
          if (!trade) throw new Error('missing trade')

          setTraceData('slippageTolerance', options.slippageTolerance.toFixed(2))

          const { calldata, value } = SwapRouter.swapERC20CallParameters(trade, {
            slippageTolerance: options.slippageTolerance,
            deadlineOrPreviousBlockhash: options.deadline?.toString(),
            inputTokenPermit: options.permit,
            // Note: SDK doesn't implement fee option, we inject it manually below
          })

          const routerAddress = UNIVERSAL_ROUTER_ADDRESS[chainId]

          const data = options.feeOptions
            ? (() => {
                const inputToken = getInputTokenInfo(trade)

                setTraceData('interfaceFee', {
                  type: 'input',
                  token: inputToken.address,
                  recipient: options.feeOptions.recipient,
                  feeBips: options.feeOptions.fee.multiply(10000).quotient.toString(),
                })

                return injectInputFee({
                  calldata,
                  inputToken,
                  feeConfig: {
                    fee: options.feeOptions.fee,
                    recipient: options.feeOptions.recipient,
                  },
                  routerAddress,
                })
              })()
            : calldata

          const tx = {
            from: account,
            to: UNIVERSAL_ROUTER_ADDRESS[chainId],
            data,
            ...(value && !isZero(value) ? { value: toHex(value) } : {}),
          }

          let gasEstimate: BigNumber
          try {
            gasEstimate = await provider.estimateGas(tx)
          } catch (gasError) {
            setTraceStatus('failed_precondition')
            setTraceError(gasError)
            console.warn(gasError)
            throw new GasEstimationError()
          }

          const gasLimit = calculateGasMargin(gasEstimate)
          setTraceData('gasLimit', gasLimit.toNumber())

          return await provider.sendTransaction({ ...tx, gasLimit }).then((response) => {
            if (tx.data !== response.data) {
              throw new ModifiedSwapError()
            }
            return response
          })
        } catch (swapError: unknown) {
          console.log('swapError', swapError)
          if (swapError instanceof ModifiedSwapError) throw swapError

          if (didUserReject(swapError)) setTraceStatus('cancelled')

          if (!(swapError instanceof GasEstimationError)) setTraceError(swapError)

          throw new Error(swapErrorToUserReadableMessage(swapError))
        }
      },
      { tags: { is_widget: false } }
    )
  }, [
    account,
    chainId,
    options.deadline,
    options.feeOptions,
    options.permit,
    options.slippageTolerance,
    provider,
    trade,
  ])
}
